// screen: Integrations — API keys + webhooks + event export (Studio spec §4.13; control-plane M4: /v1/api-keys, /v1/webhooks*,
// docs/webhooks.md). Every action is a documented API route (Studio spec §12 rule 1).
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Bot, BookOpen, ChevronDown, KeyRound, RotateCcw, Send, Trash2, Webhook } from 'lucide-react';
import type { ApiError } from '@viewstream/api-client';
import { fmtDateTime } from '@viewstream/i18n';
import {
  Badge, Button, Checkbox, ConfirmByNameDialog, EmptyState, ErrorState, Field, Input, Skeleton, Switch, useToast,
} from '@viewstream/ui';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import {
  useCreateWebhook, useDeleteWebhook, useDeliveries, usePatchWebhook, useRedeliver, useTestWebhook, useWebhooks,
  type WebhookDelivery, type WebhookEndpoint,
} from '~/app/webhooks';
import { useApiKeys, useCreateApiKey, useRevokeApiKey, type ApiKey } from '~/app/apikeys';
import { problemText } from '~/app/team';
import { EventExport } from '~/app/EventExport';

export const Route = createFileRoute('/_layout/t/$slug/integrations')({ component: Screen });

// Public API reference (Swagger UI, change api-docs): every route with its required scope, try-it-out with the user's own key
const API_DOCS_URL = 'https://api.viewstream.co.il/docs/';
// MCP server for AI assistants (docs/guide/api/mcp.md, help topic api-mcp): an API key as a bearer token, same scopes
const MCP_URL = 'https://api.viewstream.co.il/mcp';
const MCP_ADD = `claude mcp add --transport http viewstream ${MCP_URL} --header "Authorization: Bearer <key>"`;

const TONE: Record<WebhookDelivery['status'], 'ok' | 'warn' | 'danger' | 'neutral'> = { delivered: 'ok', pending: 'neutral', failed: 'warn', dead: 'danger' };

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  React.useEffect(() => { document.title = appTitle(t`Integrations`); }, [t]);
  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold">{t`Integrations`}</h1>
      <section aria-labelledby="keys-h" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="keys-h" className="text-lg font-semibold">{t`API keys`}</h2>
          <a className="inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline" href={API_DOCS_URL} target="_blank" rel="noreferrer">
            <BookOpen className="size-4" aria-hidden />{t`API docs`}<span className="sr-only">{t`(opens in a new tab)`}</span>
          </a>
        </div>
        <p className="text-sm text-fg-muted">{t`Keys let your CMS call the ViewStream API. A key is shown once when created and can never grant more than your own permissions.`}</p>
        {can(scopes, 'keys:manage') ? <ApiKeys tenant={tenant.slug} /> : <p className="text-sm text-fg-muted">{t`Your role cannot manage API keys.`}</p>}
      </section>
      <section aria-labelledby="mcp-h" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="mcp-h" className="flex items-center gap-2 text-lg font-semibold"><Bot className="size-5" aria-hidden />{t`AI assistants (MCP)`}</h2>
          <Link className="inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline" to={'/t/$slug/help/$topic' as never} params={{ slug: tenant.slug, topic: 'api-mcp' } as never}>
            <BookOpen className="size-4" aria-hidden />{t`MCP guide`}
          </Link>
        </div>
        <p className="text-sm text-fg-muted">{t`Connect Claude, ChatGPT, Cursor or another AI assistant to ViewStream over MCP. The assistant uses an API key from above and can do only what the key's scopes allow; every call is recorded in the audit log. Use a separate key with read-only scopes unless the assistant should change things.`}</p>
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{t`MCP server URL`}</span>
            <code dir="ltr" className="rounded bg-surface-2 px-2 py-1 font-mono text-sm ltr-isolate">{MCP_URL}</code>
            <Button variant="secondary" size="sm" onClick={() => { void navigator.clipboard?.writeText(MCP_URL); }}>{t`Copy`}</Button>
          </div>
          <p className="text-sm text-fg-muted">{t`Claude Code:`}</p>
          <div className="flex flex-wrap items-start gap-2">
            <pre dir="ltr" className="min-w-0 flex-1 overflow-x-auto rounded bg-surface-2 p-2 text-xs">{MCP_ADD}</pre>
            <Button variant="secondary" size="sm" onClick={() => { void navigator.clipboard?.writeText(MCP_ADD); }}>{t`Copy`}</Button>
          </div>
        </div>
      </section>
      <section aria-labelledby="hooks-h" className="space-y-3">
        <h2 id="hooks-h" className="text-lg font-semibold">{t`Webhooks`}</h2>
        <p className="text-sm text-fg-muted">{t`Your CMS receives signed POSTs (X-VS-Signature, HMAC-SHA256) for the events you choose, retried for 12 hours. Deliveries run in parallel, so events can arrive out of order — use the "at" field.`}</p>
        {can(scopes, 'webhooks:manage') ? <Webhooks tenant={tenant.slug} /> : <p className="text-sm text-fg-muted">{t`Your role cannot manage webhooks.`}</p>}
      </section>
      <section aria-labelledby="export-h" className="space-y-3">
        <h2 id="export-h" className="text-lg font-semibold">{t`Event export`}</h2>
        <p className="text-sm text-fg-muted">{t`Stream viewing statistics (player, ads, site) and platform events to your own endpoint, an S3 bucket, a Kafka topic or Google Analytics 4, in batches. Only the hashed anonymous viewer id is sent (none without consent) — never the IP address.`}</p>
        {can(scopes, 'webhooks:manage') ? <EventExport tenant={tenant.slug} scopes={scopes} /> : <p className="text-sm text-fg-muted">{t`Your role cannot manage event export.`}</p>}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- API keys

function ApiKeys({ tenant }: { tenant: string }) {
  const { t } = useLingui();
  const { locale, tz } = useLocale();
  const q = useApiKeys(tenant);
  const [revoking, setRevoking] = React.useState<ApiKey | undefined>();
  const revoke = useRevokeApiKey(tenant);
  const at = (iso: string | null) => (iso ? fmtDateTime(iso, { locale, tz }, { dateStyle: 'short', timeStyle: 'short' }) : '—');
  if (q.isError) return <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load API keys`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  if (q.isPending) return <Skeleton className="h-24" />;
  const active = q.data.items.filter((k) => !k.revoked_at);
  return (
    <div className="space-y-4">
      <CreateKeyForm tenant={tenant} grantable={q.data.scopes} />
      {active.length === 0 ? (
        <EmptyState icon={<KeyRound className="size-8" aria-hidden />} title={t`No API keys yet`} />
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-fg-muted">
            <tr><th className="text-start">{t`Name`}</th><th className="text-start">{t`Key`}</th><th className="text-start">{t`Scopes`}</th><th className="text-start">{t`Last used`}</th><th className="text-start">{t`Created`}</th><th><span className="sr-only">{t`Actions`}</span></th></tr>
          </thead>
          <tbody>
            {active.map((k) => (
              <tr key={k.id} className="border-t border-border align-top">
                <td className="py-2">{k.name || '—'}</td>
                <td><code className="ltr-isolate">{k.key_prefix}…</code></td>
                <td className="max-w-80"><span className="ltr-isolate text-xs">{k.scopes.join(' ')}</span></td>
                <td>{at(k.last_used_at)}</td>
                <td>{at(k.created_at)}</td>
                <td className="text-end">
                  <Button variant="ghost" size="sm" onClick={() => setRevoking(k)}><Trash2 className="size-4" aria-hidden /> {t`Revoke`}</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {revoking ? (
        <ConfirmByNameDialog open onOpenChange={(o) => { if (!o) setRevoking(undefined); }} name={revoking.key_prefix}
          title={t`Revoke API key`} description={t`Every system using this key stops working immediately. Type the key prefix to confirm.`}
          labels={{ field: t`Key prefix`, confirm: t`Revoke`, cancel: t`Cancel`, close: t`Close` }} busy={revoke.isPending}
          onConfirm={() => revoke.mutateAsync(revoking.id).then(() => setRevoking(undefined))} />
      ) : null}
    </div>
  );
}

function CreateKeyForm({ tenant, grantable }: { tenant: string; grantable: string[] }) {
  const { t } = useLingui();
  const create = useCreateApiKey(tenant);
  const [name, setName] = React.useState('');
  const [picked, setPicked] = React.useState<string[]>([]);
  const [plain, setPlain] = React.useState<string | undefined>();
  const [err, setErr] = React.useState<string | undefined>();
  const toggle = (s: string, on: boolean) => setPicked((p) => (on ? [...p, s] : p.filter((x) => x !== s)));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(undefined);
    create.mutate({ name: name.trim(), scopes: picked }, {
      onSuccess: (v) => { setName(''); setPicked([]); setPlain(v.key); },
      onError: (x) => setErr((x as ApiError).problem?.detail ?? (x as Error).message),
    });
  };
  return (
    <div className="space-y-2 rounded-lg border border-border p-3">
      <form onSubmit={submit} className="space-y-3">
        <Field id="key-name" label={t`Key name`} error={err}>
          <Input required maxLength={120} placeholder={t`e.g. CMS production`} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <fieldset>
          <legend className="mb-1 text-sm font-medium">{t`Scopes`}</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {grantable.map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm">
                <Checkbox checked={picked.includes(s)} onCheckedChange={(v) => toggle(s, v === true)} />
                <code className="ltr-isolate">{s}</code>
              </label>
            ))}
          </div>
        </fieldset>
        <Button type="submit" disabled={create.isPending || !name.trim() || picked.length === 0}>{t`Create key`}</Button>
      </form>
      {plain ? (
        <div role="status" className="rounded border border-warn/30 bg-warn/10 p-2 text-sm">
          <p>{t`Your new key — shown once. Copy it now and store it in your CMS:`}</p>
          <div className="mt-1 flex items-center gap-2">
            <code className="ltr-isolate break-all">{plain}</code>
            <Button variant="secondary" size="sm" onClick={() => { void navigator.clipboard?.writeText(plain); }}>{t`Copy`}</Button>
            <Button variant="ghost" size="sm" onClick={() => setPlain(undefined)}>{t`Done`}</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- webhooks

function Webhooks({ tenant }: { tenant: string }) {
  const { t } = useLingui();
  const list = useWebhooks(tenant);
  const [sel, setSel] = React.useState<string | undefined>();
  if (list.isError) return <ErrorState error={list.error as ApiError} onRetry={() => list.refetch()} labels={{ title: t`Could not load webhooks`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  if (list.isPending) return <Skeleton className="h-32" />;
  const items = list.data.items;
  const open = items.find((e) => e.id === sel) ?? items[0];
  return (
    <div className="space-y-4">
      <CreateWebhookForm tenant={tenant} eventTypes={list.data.event_types ?? []} />
      {items.length === 0 ? (
        <EmptyState icon={<Webhook className="size-8" aria-hidden />} title={t`No webhook endpoints yet`} />
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border" aria-label={t`Webhook endpoints`}>
          {items.map((e) => <EndpointRow key={e.id} tenant={tenant} ep={e} selected={e.id === open?.id} onSelect={() => setSel(e.id)} />)}
        </ul>
      )}
      {open ? <DeliveryLog tenant={tenant} ep={open} /> : null}
    </div>
  );
}

function CreateWebhookForm({ tenant, eventTypes }: { tenant: string; eventTypes: string[] }) {
  const { t } = useLingui();
  const create = useCreateWebhook(tenant);
  const [url, setUrl] = React.useState('');
  const [all, setAll] = React.useState(true);
  const [events, setEvents] = React.useState<string[]>([]);
  const [secret, setSecret] = React.useState<string | undefined>();
  const [err, setErr] = React.useState<string | undefined>();
  const toggle = (ev: string, on: boolean) => setEvents((p) => (on ? [...p, ev] : p.filter((x) => x !== ev)));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(undefined);
    create.mutate({ url: url.trim(), events: all ? ['*'] : events }, {
      onSuccess: (v) => { setUrl(''); setEvents([]); setAll(true); setSecret(v.secret); },
      onError: (x) => setErr(problemText(x)), // the SSRF guard's reason is a field error on `url`
    });
  };
  return (
    <div className="space-y-2 rounded-lg border border-border p-3">
      <form onSubmit={submit} className="space-y-3">
        <Field id="wh-url" label={t`Endpoint URL`} error={err}>
          <Input type="url" required dir="ltr" placeholder="https://cms.example.co.il/hooks/viewstream" value={url} onChange={(e) => setUrl(e.target.value)} />
        </Field>
        <fieldset>
          <legend className="mb-1 text-sm font-medium">{t`Events`}</legend>
          <label className="mb-1 flex items-center gap-2 text-sm">
            <Checkbox checked={all} onCheckedChange={(v) => setAll(v === true)} /> {t`All events`}
          </label>
          {!all ? (
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {eventTypes.filter((x) => x !== '*').map((ev) => (
                <label key={ev} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={events.includes(ev)} onCheckedChange={(v) => toggle(ev, v === true)} />
                  <code className="ltr-isolate">{ev}</code>
                </label>
              ))}
            </div>
          ) : null}
        </fieldset>
        <Button type="submit" disabled={create.isPending || !url.trim() || (!all && events.length === 0)}>{t`Add endpoint`}</Button>
      </form>
      {secret ? (
        <div role="status" className="rounded border border-warn/30 bg-warn/10 p-2 text-sm">
          <p>{t`Signing secret — shown once, store it in your CMS now:`}</p>
          <div className="mt-1 flex items-center gap-2">
            <code className="ltr-isolate break-all">{secret}</code>
            <Button variant="secondary" size="sm" onClick={() => { void navigator.clipboard?.writeText(secret); }}>{t`Copy`}</Button>
            <Button variant="ghost" size="sm" onClick={() => setSecret(undefined)}>{t`Done`}</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function EndpointRow({ tenant, ep, selected, onSelect }: { tenant: string; ep: WebhookEndpoint; selected: boolean; onSelect: () => void }) {
  const { t } = useLingui();
  const toast = useToast();
  const patch = usePatchWebhook(tenant);
  const del = useDeleteWebhook(tenant);
  const test = useTestWebhook(tenant);
  const [confirm, setConfirm] = React.useState(false);
  return (
    <li className={`flex flex-wrap items-center gap-3 p-3 ${selected ? 'bg-surface-2' : ''}`}>
      <button type="button" className="min-w-0 flex-1 text-start" onClick={onSelect} aria-pressed={selected}>
        <span className="block truncate font-mono text-sm ltr-isolate">{ep.url}</span>
        <span className="text-xs text-fg-muted ltr-isolate">{ep.events.join(' ')} · {ep.secret_hint}</span>
      </button>
      <Switch checked={ep.enabled} aria-label={t`Enabled`} onCheckedChange={(v) => patch.mutate({ id: ep.id, enabled: v })} />
      <Button variant="secondary" size="sm" onClick={() => test.mutate(ep.id, { onSuccess: () => { onSelect(); toast.push({ title: t`Test ping queued`, tone: 'ok' }); } })}>
        <Send className="size-4" aria-hidden /> {t`Send test`}
      </Button>
      <Button variant="ghost" size="icon" aria-label={t`Delete endpoint`} onClick={() => setConfirm(true)}>
        <Trash2 className="size-4" aria-hidden />
      </Button>
      {confirm ? (
        <ConfirmByNameDialog open onOpenChange={setConfirm} name={ep.url}
          title={t`Delete webhook endpoint`} description={t`The endpoint and its delivery log are deleted. Type the URL to confirm.`}
          labels={{ field: t`Endpoint URL`, confirm: t`Delete`, cancel: t`Cancel`, close: t`Close` }} busy={del.isPending}
          onConfirm={() => del.mutateAsync(ep.id).then(() => setConfirm(false))} />
      ) : null}
    </li>
  );
}

function DeliveryLog({ tenant, ep }: { tenant: string; ep: WebhookEndpoint }) {
  const { t } = useLingui();
  const { locale, tz } = useLocale();
  const q = useDeliveries(tenant, ep.id);
  const redeliver = useRedeliver(tenant);
  const toast = useToast();
  const [openId, setOpenId] = React.useState<string | undefined>();
  const at = (iso: string) => fmtDateTime(iso, { locale, tz }, { dateStyle: 'short', timeStyle: 'medium' });
  return (
    <div>
      <h3 className="mb-2 font-semibold">{t`Delivery log`} <span className="font-mono text-xs font-normal text-fg-muted ltr-isolate">{ep.url}</span></h3>
      {q.isPending ? <Skeleton className="h-24" /> : (q.data ?? []).length === 0 ? <p className="text-sm text-fg-muted">{t`No deliveries yet.`}</p> : (
        <table className="w-full text-sm">
          <thead className="text-xs text-fg-muted">
            <tr><th className="text-start">{t`Time`}</th><th className="text-start">{t`Event`}</th><th className="text-start">{t`Status`}</th><th className="text-start">{t`Attempts`}</th><th className="text-start">{t`Response`}</th><th><span className="sr-only">{t`Actions`}</span></th></tr>
          </thead>
          <tbody>
            {(q.data ?? []).map((d) => (
              <React.Fragment key={d.id}>
                <tr className="border-t border-border">
                  <td className="py-1">
                    <button type="button" className="inline-flex items-center gap-1" aria-expanded={openId === d.id} onClick={() => setOpenId(openId === d.id ? undefined : d.id)}>
                      <ChevronDown className={`size-3 ${openId === d.id ? '' : 'icon-dir -rotate-90'}`} aria-hidden /> {at(d.created_at)}
                    </button>
                  </td>
                  <td><code className="ltr-isolate">{d.event_type}</code></td>
                  <td><Badge tone={TONE[d.status]}>{d.status}</Badge></td>
                  <td>{d.attempts}</td>
                  <td className="ltr-isolate">{d.last_status_code ?? '—'}{d.response_ms != null ? ` · ${d.response_ms} ms` : ''}{d.last_error && d.status !== 'delivered' ? ` · ${d.last_error}` : ''}</td>
                  <td className="text-end">
                    <Button variant="ghost" size="sm" disabled={redeliver.isPending}
                      onClick={() => redeliver.mutate({ id: ep.id, delivery: d.id }, { onSuccess: () => toast.push({ title: t`Delivery queued again`, tone: 'ok' }) })}>
                      <RotateCcw className="size-4" aria-hidden /> {t`Redeliver`}
                    </Button>
                  </td>
                </tr>
                {openId === d.id ? (
                  <tr><td colSpan={6} className="pb-2">
                    <pre dir="ltr" className="max-h-72 overflow-auto rounded bg-surface-2 p-2 text-xs" aria-label={t`Payload`}>{JSON.stringify(d.payload ?? null, null, 2)}</pre>
                  </td></tr>
                ) : null}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
